import * as Sentry from "@sentry/react";
import { clearSession, getSession } from "./auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

interface FetchOpts {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  /** Skip adding the Authorization header (used for /auth/login). */
  noAuth?: boolean;
}

async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  if (!opts.noAuth) {
    const session = getSession();
    if (session) {
      headers["authorization"] = `Bearer ${session.token}`;
    }
  }

  const resp = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // Tag the active Sentry scope with the request_id (echoed from the Worker's
  // cf-ray) so a captured error can be cross-referenced to the matching
  // [ray=...] log line. See worker/src/router.rs::route — the X-Request-Id
  // header is set on every response when cf-ray is present.
  const requestId = resp.headers.get("x-request-id");
  if (requestId) {
    Sentry.getCurrentScope().setTag("request_id", requestId);
  }

  // 401 → token is bad/expired. Clear and bounce to login.
  if (resp.status === 401 && !opts.noAuth) {
    clearSession();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }

  if (!resp.ok) {
    let message = `HTTP ${resp.status}`;
    try {
      const data = await resp.json();
      if (data && typeof data.error === "string") message = data.error;
    } catch {
      // body may not be JSON; fall back to the status code.
    }
    throw new ApiError(resp.status, message);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// --- Auth ---

export async function login(email: string, password: string) {
  return request<{ token: string; role: "admin" | "scan"; expires_at: number }>(
    "/auth/login",
    { method: "POST", body: { email, password }, noAuth: true },
  );
}

export async function whoami() {
  return request<{ user_id: string; email: string | null; role: string }>("/auth/me");
}

// --- Scan ---

import type { Album, Book, Movie } from "@shared/types";

export async function scanBook(isbn: string) {
  return request<{ book: Book; cover_url: string | null }>("/scan/book", {
    method: "POST",
    body: { isbn },
  });
}

export async function scanAlbum(barcode: string) {
  return request<{ album: Album }>("/scan/album", {
    method: "POST",
    body: { barcode },
  });
}

export async function searchMovies(query: string) {
  return request<{ results: Movie[] }>("/scan/movie/search", {
    method: "POST",
    body: { query },
  });
}

export async function getMovieDetails(tmdbId: number, mediaType: "movie" | "series") {
  return request<{ movie: Movie }>(`/scan/movie/${tmdbId}`, {
    method: "POST",
    body: { media_type: mediaType },
  });
}

// --- Catalog ---

export async function listBooks(params: { q?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  return request<{ books: Book[]; total: number }>(`/books?${qs.toString()}`);
}

export async function listAlbums(params: { q?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  return request<{ albums: Album[]; total: number }>(`/albums?${qs.toString()}`);
}

export async function listMovies(params: { q?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  return request<{ movies: Movie[]; total: number }>(`/movies?${qs.toString()}`);
}

export async function createBook(book: Book) {
  return request<Book>("/books", { method: "POST", body: book });
}

export async function createAlbum(album: Album) {
  return request<Album>("/albums", { method: "POST", body: album });
}

export async function createMovie(movie: Movie) {
  return request<Movie>("/movies", { method: "POST", body: movie });
}
